import { ReactRenderer } from '@tiptap/react'
import type { SuggestionProps } from '@tiptap/suggestion'
import tippy, { type Instance as TippyInstance } from 'tippy.js'

import type { MentionItem } from '../../markdown-editor.interface'
import { MentionList, type MentionListRef } from './mention-list.molecule'

export interface SuggestionRendererConfig {
  onSelect?: (item: MentionItem) => void
}

interface RendererState {
  component: ReactRenderer<MentionListRef> | null
  popup: TippyInstance[] | null
}

const createComponent = (props: SuggestionProps<MentionItem>, config: SuggestionRendererConfig) =>
  new ReactRenderer(MentionList, {
    props: {
      ...props,
      command: (item: MentionItem) => {
        props.command(item)
        config.onSelect?.(item)
      },
    },
    editor: props.editor,
  })

const createPopup = (props: SuggestionProps<MentionItem>, element: Element): TippyInstance[] =>
  tippy('body', {
    getReferenceClientRect: props.clientRect as () => DOMRect,
    appendTo: () => document.body,
    content: element,
    showOnCreate: true,
    interactive: true,
    trigger: 'manual',
    placement: 'bottom-start',
    maxWidth: 'none',
    theme: 'mention',
    arrow: false,
  })

export const createSuggestionRenderer = (config: SuggestionRendererConfig) => {
  const state: RendererState = { component: null, popup: null }

  return {
    onStart: (props: SuggestionProps<MentionItem>) => {
      state.component = createComponent(props, config)
      if (!props.clientRect) return
      state.popup = createPopup(props, state.component.element)
    },

    onUpdate: (props: SuggestionProps<MentionItem>) => {
      state.component?.updateProps({
        ...props,
        command: (item: MentionItem) => {
          props.command(item)
          config.onSelect?.(item)
        },
      })
      if (!props.clientRect) return
      state.popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect as () => DOMRect })
    },

    onKeyDown: (props: { event: KeyboardEvent }) => {
      if (props.event.key === 'Escape') {
        state.popup?.[0]?.hide()
        return true
      }
      return state.component?.ref?.onKeyDown(props) ?? false
    },

    onExit: () => {
      state.popup?.[0]?.destroy()
      state.component?.destroy()
    },
  }
}
